import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin-bottom: 10px !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
`;
export const HeroActions = styled.div `
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;
// ============================================
// STATS GRID
// ============================================
export const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: -24px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    margin-top: -16px;
  }
`;
export const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));

    &::before {
      opacity: 1;
    }
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 32px;
    font-weight: 600;
    color: var(--accent-gold, #B8860B);
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-secondary);
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 48px;
`;
// ============================================
// SEARCH & FILTER BAR
// ============================================
export const SearchFilterBar = styled.div `
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
export const SearchInputWrapper = styled.div `
  flex: 1;
`;
export const FilterSelectWrapper = styled.div `
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;
export const InputLabel = styled.label `
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;
export const SearchInput = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.08));
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
export const FilterSelect = styled.select `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
// ============================================
// SECTION TITLE
// ============================================
export const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 20px 0 !important;
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    color: var(--accent-gold, #B8860B);
    font-size: 16px;
  }
`;
// ============================================
// USER CARDS
// ============================================
export const UsersGrid = styled.div `
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
export const UserCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${fadeUp} 0.5s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }
`;
export const UserCardHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;
export const UserInfo = styled.div `
  flex: 1;
`;
export const UserName = styled.h4 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 4px 0 !important;
`;
export const UserEmail = styled.p `
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 4px 0;
`;
export const UserBusiness = styled.p `
  font-size: 14px;
  color: var(--text-tertiary);
  font-style: italic;
  margin: 0;
`;
export const UserBadges = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;
export const StatusBadge = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ $status }) => {
    switch ($status) {
        case 'active':
            return `
          background: rgba(22, 163, 74, 0.1);
          color: #16a34a;
          border: 1px solid rgba(22, 163, 74, 0.3);
        `;
        case 'suspended':
            return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        `;
        case 'pending':
            return `
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.3);
        `;
        default:
            return `
          background: var(--bg-tertiary);
          color: var(--text-tertiary);
          border: 1px solid var(--border-color);
        `;
    }
}}
`;
export const RoleBadge = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ $color }) => $color ? `${$color}20` : 'var(--accent-gold-bg)'};
  color: ${({ $color }) => $color || 'var(--accent-gold)'};
  border: 1px solid ${({ $color }) => $color ? `${$color}40` : 'var(--accent-gold-border)'};
`;
export const UserCardBody = styled.div `
  padding: 20px 24px;
`;
export const UserDetails = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const DetailItem = styled.div `
  .label {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }

  .value {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 500;
  }
`;
export const UserCardActions = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-tertiary);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;
export const RoleSelector = styled.select `
  padding: 10px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
// ============================================
// BUTTONS
// ============================================
export const PrimaryButton = styled.button `
  background: var(--accent-gold, #B8860B);
  color: #FFFFFF;
  border: 1px solid var(--accent-gold, #B8860B);
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-gold-dark, #8B6914);
    border-color: var(--accent-gold-dark, #8B6914);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const SecondaryButton = styled.button `
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: var(--accent-gold, #B8860B);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const ActionButton = styled.button `
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $variant }) => {
    switch ($variant) {
        case 'primary':
            return `
          background: var(--accent-gold, #B8860B);
          color: #FFFFFF;
          border: 1px solid var(--accent-gold, #B8860B);

          &:hover {
            background: var(--accent-gold-dark, #8B6914);
            border-color: var(--accent-gold-dark, #8B6914);
          }
        `;
        case 'danger':
            return `
          background: transparent;
          color: #ef4444;
          border: 1px solid #ef4444;

          &:hover {
            background: rgba(239, 68, 68, 0.1);
          }
        `;
        case 'success':
            return `
          background: transparent;
          color: #16a34a;
          border: 1px solid #16a34a;

          &:hover {
            background: rgba(22, 163, 74, 0.1);
          }
        `;
        default:
            return `
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);

          &:hover {
            border-color: var(--accent-gold, #B8860B);
            color: var(--accent-gold, #B8860B);
          }
        `;
    }
}}
`;
// ============================================
// ERROR DISPLAY
// ============================================
export const ErrorBanner = styled.div `
  padding: 16px 20px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.3);
  margin-bottom: 24px;
  color: #ef4444;

  strong {
    display: block;
    margin-bottom: 4px;
  }

  small {
    color: var(--text-tertiary);
    display: block;
    margin-top: 8px;

    code {
      background: var(--bg-tertiary);
      padding: 2px 6px;
      font-size: 12px;
    }
  }
`;
// ============================================
// EMPTY STATE
// ============================================
export const EmptyState = styled.div `
  text-align: center;
  padding: 60px 24px;
  color: var(--text-tertiary);

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 15px;
    margin: 0;
  }
`;
// ============================================
// ADMIN ACTIONS SECTION
// ============================================
export const AdminSection = styled.section `
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid var(--border-color);
`;
export const AdminSectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 20px 0 !important;
`;
export const AdminActionsGrid = styled.div `
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;
// ============================================
// LOADING STATE
// ============================================
export const LoadingWrapper = styled.div `
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;
export const LoadingSpinner = styled.div `
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-gold, #B8860B);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
export const LoadingText = styled.p `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  color: var(--text-secondary);
  margin: 0;
`;
export const LoadingSubText = styled.p `
  font-size: 14px;
  color: var(--text-tertiary);
  margin: 8px 0 0 0;
`;
